"use client";

import { useRef, useState } from "react";
import { KeyRound } from "lucide-react";
import { rotateWebhookSecret, type WebhookEndpoint } from "../../../services/merchantApi";
import { ConfirmDialog, InlineNotice } from "../ui";
import { formatAbsolute } from "./format";
import { UNAVAILABLE_MESSAGE, apiErrorOf, useCountdown } from "./EndpointFormModal";
import { notifyWebhooksChanged } from "./useWebhookSummary";

// ────────────────────────────────────────────────────────────────────────────
// RotateSecretModal — issue a new signing secret for one endpoint.
// Not optimistic: the dialog stays busy until the server answers, then the
// caller receives the patched endpoint AND the new secret (to show once).
// ────────────────────────────────────────────────────────────────────────────

export interface RotateSecretResult {
  /** The endpoint with its secret fields patched from the response. */
  endpoint: WebhookEndpoint;
  /** The new whsec_… — show it once, never store it. */
  secret: string;
  /** When the old secret stops verifying; null when it was revoked at once. */
  previousSecretExpiresAt: string | null;
  /** An earlier 24-hour overlap was still open and its older secret was dropped. */
  replacedPreviousSecret: boolean;
}

export interface RotateSecretModalProps {
  open: boolean;
  onClose: () => void;
  wallet: string | null;
  email: string | null;
  endpoint: WebhookEndpoint | null;
  /** Platform flag: secret writes are paused on our side. */
  secretWritesBlocked: boolean;
  onRotated: (result: RotateSecretResult) => void;
}

type RotateError =
  | { kind: "error"; message: string; requestId?: string | null }
  | { kind: "warn"; message: string }
  | { kind: "rate"; message: string };

/** Renders nothing while closed, so each opening starts unchecked. */
export default function RotateSecretModal(props: RotateSecretModalProps) {
  if (!props.open || !props.endpoint) return null;
  return <RotateDialog {...props} endpoint={props.endpoint} />;
}

function RotateDialog({
  onClose,
  wallet,
  email,
  endpoint,
  secretWritesBlocked,
  onRotated,
}: RotateSecretModalProps & { endpoint: WebhookEndpoint }) {
  const [revoke, setRevoke] = useState(false);
  const [busy, setBusy] = useState(false);
  const busyRef = useRef(false);
  const [error, setError] = useState<RotateError | null>(null);
  const cooldown = useCountdown();

  const overlapOpen =
    !!endpoint.previousSecretExpiresAt &&
    new Date(endpoint.previousSecretExpiresAt).getTime() > Date.now();

  const confirm = async () => {
    if (busyRef.current || cooldown.active || secretWritesBlocked) return;
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const res = await rotateWebhookSecret(wallet, email, endpoint.id, { revokePreviousNow: revoke });
      const next: WebhookEndpoint = {
        ...endpoint,
        secretLast4: res.secretLast4,
        secretVersion: res.secretVersion,
        secretCreatedAt: new Date().toISOString(),
        // The secret that was current a moment ago is the one still overlapping.
        previousSecretLast4: res.previousSecretExpiresAt ? endpoint.secretLast4 : null,
        previousSecretExpiresAt: res.previousSecretExpiresAt,
      };
      onRotated({
        endpoint: next,
        secret: res.secret,
        previousSecretExpiresAt: res.previousSecretExpiresAt,
        replacedPreviousSecret: !!res.replacedPreviousSecret,
      });
      onClose();
    } catch (err) {
      const e = apiErrorOf(err);
      if (e.status === 429) {
        cooldown.start(e.retryAfter);
        setError({ kind: "rate", message: e.message || "This secret was rotated a moment ago." });
      } else if (e.code === "webhooks_unavailable") {
        setError({ kind: "warn", message: UNAVAILABLE_MESSAGE });
      } else {
        setError({
          kind: "error",
          message: e.message || "Couldn't rotate the secret. Please try again.",
          requestId: e.requestId,
        });
      }
    } finally {
      busyRef.current = false;
      setBusy(false);
      notifyWebhooksChanged();
    }
  };

  const description = revoke
    ? "You'll see the new secret once. The current secret stops working immediately, so deliveries fail verification until your server has the new one."
    : "You'll see the new secret once. For the next 24 hours every delivery is signed with BOTH secrets, so you can deploy the new one without downtime.";

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      onConfirm={confirm}
      busy={busy}
      title="Rotate signing secret?"
      description={description}
      confirmLabel={cooldown.active ? cooldown.text : revoke ? "Rotate and revoke" : "Rotate secret"}
      confirmDisabled={cooldown.active || secretWritesBlocked}
      variant={revoke ? "danger" : "default"}
      icon={<KeyRound className="w-5 h-5" />}
    >
      <div className="space-y-3">
        <p className="text-xs text-gray-500">
          Current secret{" "}
          <span className="font-mono text-gray-300">••••{endpoint.secretLast4}</span>{" "}
          <span className="text-gray-600">(v{endpoint.secretVersion})</span> on{" "}
          <span className="text-gray-300 break-all">{endpoint.urlHost}</span>
        </p>

        {overlapOpen && !revoke && (
          <InlineNotice tone="warn">
            A previous rotation is still in its 24-hour window. Rotating again drops the older secret (
            <span className="font-mono">••••{endpoint.previousSecretLast4 || "????"}</span>) immediately
            {endpoint.previousSecretExpiresAt
              ? ` instead of at ${formatAbsolute(endpoint.previousSecretExpiresAt)}`
              : ""}
            .
          </InlineNotice>
        )}

        <label
          className={`flex items-start gap-3 p-3 rounded-xl border transition-colors ${
            revoke ? "border-red-500/30 bg-red-500/5" : "border-gray-800/60 bg-gray-900/30"
          } ${busy ? "cursor-not-allowed" : "cursor-pointer"}`}
        >
          <input
            type="checkbox"
            checked={revoke}
            onChange={(e) => setRevoke(e.target.checked)}
            disabled={busy}
            className="mt-0.5 w-4 h-4 shrink-0 accent-red-500"
          />
          <span className="text-sm text-gray-400 leading-relaxed">
            <span className="text-white font-medium">Revoke the current secret immediately</span> (use if it
            leaked). Deliveries will fail verification until your server has the new secret.
          </span>
        </label>

        {secretWritesBlocked && (
          <InlineNotice tone="warn" role="status">
            Rotating secrets is paused on our side right now. Your current secret keeps working and nothing
            is lost — please try again later.
          </InlineNotice>
        )}

        {error && (error.kind !== "rate" || cooldown.active) && (
          <InlineNotice
            tone={error.kind === "error" ? "error" : "warn"}
            role="alert"
            title={error.kind === "rate" ? "Not yet" : undefined}
          >
            {error.message}
            {error.kind === "rate" && <span className="block mt-1 tabular-nums">{cooldown.text}.</span>}
            {error.kind === "error" && error.requestId && (
              <span className="block mt-1 text-[11px] font-mono opacity-70 break-all">
                Request ID · {error.requestId}
              </span>
            )}
          </InlineNotice>
        )}
      </div>
    </ConfirmDialog>
  );
}
